// Cofre: criptografia de ponta a ponta (AES-256-GCM) para dados sensíveis (números de documentos, exames, histórico de saúde).
// Uma chave de dados aleatória cifra tudo; essa chave fica guardada na conta só "embrulhada" pela senha do cofre
// (PBKDF2-SHA256). A senha nunca sai do aparelho e o servidor só vê texto cifrado. Sem a senha, ninguém abre, nem o suporte.
import { useSyncExternalStore } from 'react';
import { gcm } from '@noble/ciphers/aes';
import { pbkdf2Async } from '@noble/hashes/pbkdf2';
import { sha256 } from '@noble/hashes/sha2';
import { getDoc, setDoc } from '../store/store';

const ITER = 60000;

/** Bytes aleatórios seguros: do sistema (expo-crypto ou crypto do navegador). */
let extra = 0;
export function getRandomBytes(n: number): Uint8Array {
  try { const m = require('expo-crypto'); if (m?.getRandomBytes) return m.getRandomBytes(n); } catch { /* sem o módulo nativo */ }
  const g = (globalThis as any).crypto;
  if (g?.getRandomValues) return g.getRandomValues(new Uint8Array(n));
  // último recurso (aparelhos muito antigos): mistura de relógio e Math.random passada pelo SHA-256
  const semente = utf8(`${Date.now()}:${Math.random()}:${Math.random()}:${extra++}:${typeof performance !== 'undefined' ? performance.now() : 0}`);
  const out = new Uint8Array(n);
  let h = sha256(semente);
  for (let i = 0; i < n; i++) { if (i % 32 === 0 && i) h = sha256(new Uint8Array([...h, i])); out[i] = h[i % 32]; }
  return out;
}
const PREFIXO = 'c1:';
const TESTE = 'myplanner-cofre-ok';

// ─── base64 e texto (sem depender do Buffer) ───
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
export function paraB64(b: Uint8Array) {
  let s = '';
  for (let i = 0; i < b.length; i += 3) {
    const n = (b[i] << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    s += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + (i + 1 < b.length ? B64[(n >> 6) & 63] : '=') + (i + 2 < b.length ? B64[n & 63] : '=');
  }
  return s;
}
export function deB64(s: string) {
  const limpo = s.replace(/[^A-Za-z0-9+/]/g, '');
  const out = new Uint8Array(Math.floor((limpo.length * 3) / 4));
  let j = 0;
  for (let i = 0; i < limpo.length; i += 4) {
    const n = (B64.indexOf(limpo[i]) << 18) | (B64.indexOf(limpo[i + 1]) << 12) | ((B64.indexOf(limpo[i + 2] ?? 'A') & 63) << 6) | (B64.indexOf(limpo[i + 3] ?? 'A') & 63);
    out[j++] = (n >> 16) & 255;
    if (i + 2 < limpo.length) out[j++] = (n >> 8) & 255;
    if (i + 3 < limpo.length) out[j++] = n & 255;
  }
  return out.slice(0, j);
}
export function utf8(s: string) {
  const out: number[] = [];
  for (const ch of s) {
    let c = ch.codePointAt(0)!;
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else { out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63)); c = 0; }
  }
  return new Uint8Array(out);
}
export function deUtf8(b: Uint8Array) {
  let s = '';
  for (let i = 0; i < b.length;) {
    const x = b[i];
    let c: number;
    if (x < 0x80) { c = x; i += 1; }
    else if (x < 0xe0) { c = ((x & 31) << 6) | (b[i + 1] & 63); i += 2; }
    else if (x < 0xf0) { c = ((x & 15) << 12) | ((b[i + 1] & 63) << 6) | (b[i + 2] & 63); i += 3; }
    else { c = ((x & 7) << 18) | ((b[i + 1] & 63) << 12) | ((b[i + 2] & 63) << 6) | (b[i + 3] & 63); i += 4; }
    s += String.fromCodePoint(c);
  }
  return s;
}

// ─── estado: a chave aberta fica só na memória e fecha sozinha ───
let chave: Uint8Array | null = null;
let fechaEm: ReturnType<typeof setTimeout> | null = null;
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };
const MINUTOS_ABERTO = 5;

function manterAberto() {
  if (fechaEm) clearTimeout(fechaEm);
  fechaEm = setTimeout(fecharCofre, MINUTOS_ABERTO * 60000);
}
export function fecharCofre() {
  if (chave) chave.fill(0);
  chave = null;
  if (fechaEm) clearTimeout(fechaEm);
  fechaEm = null;
  avisar();
}
export const cofreConfigurado = () => !!getDoc('ajustes').cofre;
export const cofreAberto = () => !!chave;
/** Re-renderiza quando o cofre abre ou fecha. */
export function useCofre() {
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
  return { configurado: cofreConfigurado(), aberto: cofreAberto() };
}

export const derivar = (senha: string, sal: Uint8Array, iter: number) => pbkdf2Async(sha256, utf8(senha.normalize('NFKC')), sal, { c: iter, dkLen: 32, asyncTick: 20 });

export function selar(k: Uint8Array, dados: Uint8Array) {
  const iv = getRandomBytes(12);
  const ct = gcm(k, iv).encrypt(dados);
  const tudo = new Uint8Array(iv.length + ct.length);
  tudo.set(iv); tudo.set(ct, iv.length);
  return paraB64(tudo);
}
export function abrirSelado(k: Uint8Array, b64: string) {
  const tudo = deB64(b64);
  return gcm(k, tudo.slice(0, 12)).decrypt(tudo.slice(12));
}

/** Cria o cofre com uma senha (mínimo 6 caracteres). */
export async function criarCofre(senha: string) {
  if (senha.length < 6) throw new Error('senha-curta');
  const sal = getRandomBytes(16);
  const kSenha = await derivar(senha, sal, ITER);
  const dados = getRandomBytes(32);
  const embrulhada = selar(kSenha, new Uint8Array([...dados, ...utf8(TESTE)]));
  setDoc('ajustes', d => ({ ...d, cofre: { sal: paraB64(sal), iter: ITER, chave: embrulhada } }));
  chave = dados; manterAberto(); avisar();
}

/** Abre o cofre. Devolve false se a senha estiver errada. */
export async function abrirCofre(senha: string) {
  const cf = getDoc('ajustes').cofre;
  if (!cf) return false;
  try {
    const k = await derivar(senha, deB64(cf.sal), cf.iter || ITER);
    const conteudo = abrirSelado(k, cf.chave);
    if (deUtf8(conteudo.slice(32)) !== TESTE) return false;
    chave = conteudo.slice(0, 32); manterAberto(); avisar();
    return true;
  } catch { return false; }
}

/** Troca a senha do cofre (a chave de dados continua a mesma, nada precisa ser recifrado). */
export async function trocarSenhaCofre(atual: string, nova: string) {
  if (nova.length < 6) throw new Error('senha-curta');
  if (!(await abrirCofre(atual)) || !chave) return false;
  const sal = getRandomBytes(16);
  const k = await derivar(nova, sal, ITER);
  const embrulhada = selar(k, new Uint8Array([...chave, ...utf8(TESTE)]));
  setDoc('ajustes', d => ({ ...d, cofre: { sal: paraB64(sal), iter: ITER, chave: embrulhada } }));
  return true;
}

export const cifrado = (v: unknown): v is string => typeof v === 'string' && v.startsWith(PREFIXO);

/** Cifra um texto (o cofre precisa estar aberto). Texto vazio continua vazio. */
export function cifrar(texto: string) {
  if (!texto) return '';
  if (!chave) throw new Error('cofre-fechado');
  manterAberto();
  return PREFIXO + selar(chave, utf8(texto));
}
/** Decifra; com o cofre fechado devolve null. Texto que não estava cifrado volta como está. */
export function decifrar(v: string | null | undefined): string | null {
  if (!v) return '';
  if (!cifrado(v)) return v;
  if (!chave) return null;
  manterAberto();
  try { return deUtf8(abrirSelado(chave, v.slice(PREFIXO.length))); } catch { return null; }
}

/** Cifra bytes (arquivos e fotos do cofre). Resultado: iv (12 bytes) + conteúdo cifrado. */
export function cifrarBytes(b: Uint8Array): Uint8Array {
  if (!chave) throw new Error('cofre-fechado');
  manterAberto();
  const iv = getRandomBytes(12);
  const ct = gcm(chave, iv).encrypt(b);
  const out = new Uint8Array(12 + ct.length);
  out.set(iv); out.set(ct, 12);
  return out;
}
export function decifrarBytes(b: Uint8Array): Uint8Array {
  if (!chave) throw new Error('cofre-fechado');
  manterAberto();
  return gcm(chave, b.slice(0, 12)).decrypt(b.slice(12));
}
